import React from 'react';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const divisions = [
    {
        name: "Leadership",
        color: "blue",
        icon: (
            <svg className="mr-1 inline-block h-5 w-5 align-middle" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
        ),
        roles: [
            { title: "Chairman/Founder", desc: "Oversees overall vision, strategy, and expansion." },
            { title: "CEO", desc: "Manages daily operations, business growth, and partnerships." },
            { title: "COO", desc: "Ensures the smooth delivery of healthcare services, logistics, and operational efficiency." },
            { title: "CFO", desc: "Handles budgeting, financial planning, and revenue management." },
        ]
    },
    {
        name: "Healthcare",
        color: "green",
        icon: (
            <svg className="mr-1 inline-block h-5 w-5 align-middle" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        roles: [
            { title: "Medical Director", desc: "Supervises all clinical activities and ensures regulatory compliance." },
            { title: "Hospital/Clinic Manager", desc: "Oversees hospital operations, staffing, and patient experience." },
            { title: "Chief Nursing Officer (CNO)", desc: "Manages nursing staff and patient care quality." },
            { title: "Pharmacy Manager", desc: "Oversees pharmaceutical procurement and distribution." },
            { title: "Medical Laboratory Director", desc: "Supervises diagnostic testing and lab operations." },
            { title: "Telemedicine Coordinator", desc: "Manages online consultations and digital healthcare services." },
            { title: "Home Care & Geriatric Services Manager", desc: "Oversees home-based patient care." },
        ]
    },
    {
        name: "Business",
        color: "blue",
        icon: (
            <svg className="mr-1 inline-block h-5 w-5 align-middle" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M16 3v4M8 3v4" /></svg>
        ),
        roles: [
            { title: "Business Development Manager", desc: "Identifies growth opportunities, partnerships, and funding." },
            { title: "Marketing & Branding Manager (Abia Tech Hub)", desc: "Develops and implements marketing strategies." },
            { title: "Customer Service Manager", desc: "Ensures excellent patient support and communication." },
            { title: "IT & Website Manager (Abia Tech Hub)", desc: "Manages telemedicine platform, website, and data security." },
        ]
    },
    {
        name: "Logistics",
        color: "green",
        icon: (
            <svg className="mr-1 inline-block h-5 w-5 align-middle" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="13" rx="2" /><path d="M16 3v4M8 3v4" /></svg>
        ),
        roles: [
            { title: "Supply Chain Manager", desc: "Oversees procurement and distribution of medical equipment & drugs." },
            { title: "Warehouse & Inventory Officer", desc: "Manages stock levels of pharmaceuticals and consumables." },
            { title: "Logistics and Delivery Coordinator", desc: "Ensures efficient transportation of medicines, lab samples, and supplies." },
        ]
    },
    {
        name: "Finance",
        color: "blue",
        icon: (
            <svg className="mr-1 inline-block h-5 w-5 align-middle" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        roles: [
            { title: "HR Manager", desc: "Manages recruitment, training, and staff welfare." },
            { title: "Finance & Accounting Officer", desc: "Handles billing, payroll, and insurance claims." },
            { title: "Compliance & Regulatory Officer", desc: "Ensures adherence to government healthcare regulations." },
            { title: "Legal Advisor", desc: "Manages contracts, business compliance, and legal matters." },
        ]
    },
    {
        name: "Support",
        color: "green",
        icon: (
            <svg className="mr-1 inline-block h-5 w-5 align-middle" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        roles: [
            { title: "Doctors, Nurses, and Healthcare Workers", desc: "The Core Patient Care Team." },
            { title: "Customer Care Representatives", desc: "Handle patient inquiries and complaints." },
            { title: "Technicians and IT Support", desc: "Ensures smooth operation of telemedicine and IT systems." },
            { title: "Drivers & Dispatch Riders", desc: "For mobile healthcare services and deliveries." },
            { title: "Security & Maintenance Staff", desc: "Ensures safety and smooth hospital operations." },
        ]
    },
];

const About = () => (
    <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
        {/* Hero Section */}
        <section className="from-brand-light relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br to-blue-100 py-16 md:py-24">
            <div className="relative z-10 mx-auto max-w-3xl text-center">
                <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-lg md:text-5xl">Our Story: Born from Purpose, Built for Impact</h1>
                <p className="animate-fade-in mb-6 font-redhat text-xl text-gray-700 delay-200 md:text-2xl">Bridging the gap between medical professionals and the communities they serve with innovation, empathy, and integrity.</p>
            </div>
            {/* Animated Bubbles and Cross Motif */}
            <svg className="animate-float-slow absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
            <svg className="animate-float-medium absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
            <svg className="animate-float-fast absolute bottom-10 left-1/2 z-0 h-20 w-20 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="30" fill="#004487" /></svg>
            <svg className="animate-float-cross absolute bottom-16 right-10 z-0 h-16 w-16" viewBox="0 0 64 64" fill="none"><rect x="28" y="8" width="8" height="48" rx="4" fill="#0087ca" /><rect x="8" y="28" width="48" height="8" rx="4" fill="#00b937" /></svg>
            <style>{`
        @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
        .delay-200 { animation-delay: 0.2s; }
        @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
        @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
        .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
        @keyframes float-fast { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-40px); } }
        .animate-float-fast { animation: float-fast 3.5s ease-in-out infinite; }
        @keyframes float-cross { 0%, 100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
        .animate-float-cross { animation: float-cross 6s ease-in-out infinite; }
      `}</style>
        </section>

        {/* Story Section */}
        <section className="mx-auto flex w-full max-w-6xl flex-col items-center gap-12 px-4 py-12 md:flex-row">
            {/* Text Side */}
            <div className="flex flex-1 flex-col items-start">
                <h2 className="mb-4 font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Who We Are</h2>
                <p className="mb-4 font-redhat text-lg text-gray-700">PUREMEDIFY LIMITED was founded with a simple yet profound mission: to bridge the gap between medical professionals and the communities they serve. In the face of fragmented services and inequities in care delivery, we envisioned a platform where healthcare, social advancement, and economic empowerment meet, offering solutions that are inclusive, innovative, and impactful.</p>
                <p className="mb-4 font-redhat text-lg text-gray-700">What started as a passion for health equity has evolved into a bold, cross-sectoral enterprise serving the needs of diverse communities across healthcare, agriculture, logistics, energy, technology, real estate, and lifestyle.</p>
            </div>
            {/* Image Side */}
            <div className="flex flex-1 justify-center">
                <img src={process.env.PUBLIC_URL + '/image/puremedifycom.webp'} alt="About PureMedify" className="h-72 w-full max-w-md rounded-2xl object-cover shadow-lg" />
            </div>
        </section>

        {/* Values Section */}
        <section className="w-full bg-white/80 px-4 py-12">
            <div className="mx-auto max-w-5xl">
                <h2 className="mb-8 text-center font-ubuntu text-2xl font-bold text-lochmara-blue md:text-3xl">Our Values</h2>
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
                    {/* Value 1 */}
                    <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-blue-50 to-white p-6 text-center shadow">
                        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-lochmara-blue/10">
                            <svg className="h-8 w-8 text-lochmara-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v9m0 0H7m5 0h5" /></svg>
                        </span>
                        <h3 className="mb-2 text-lg font-bold text-lochmara-blue">Purity of Purpose</h3>
                        <p className="font-redhat text-gray-600">We deliver products and services that meet the highest standards of quality, safety, and effectiveness because you deserve nothing less.</p>
                    </div>
                    {/* Value 2 */}
                    <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-green-50 to-white p-6 text-center shadow">
                        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-malachite-green/10">
                            <svg className="h-8 w-8 text-malachite-green" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </span>
                        <h3 className="mb-2 text-lg font-bold text-malachite-green">Care Without Compromise</h3>
                        <p className="font-redhat text-gray-600">Our clients and patients are our priority. Every action we take is rooted in empathy, reliability, and respect.</p>
                    </div>
                    {/* Value 3 */}
                    <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-blue-50 to-white p-6 text-center shadow">
                        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand/10">
                            <svg className="h-8 w-8 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" /></svg>
                        </span>
                        <h3 className="mb-2 text-lg font-bold text-brand">Innovation with Integrity</h3>
                        <p className="font-redhat text-gray-600">We leverage the latest technologies, scientific research, and global best practices while maintaining a foundation of ethics and transparency.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* Call to Action Section */}
        <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-12">
            <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Ready to Join Our Mission?</h2>
            <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">Whether you’re a healthcare professional, partner, or community member, we invite you to connect with us and help create a healthier, more equitable world.</p>
            <Link to="/contact" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">Contact Us</Link>
        </section>
    </div>
);

export default About;
